import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { axiosInstance } from "../utils/axios";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import { Combobox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import Button from "./Button";
import useDialogStore from "../stores/useDialogStore";
import { useQueryClient } from "@tanstack/react-query";

const clientSchema = Joi.object({
  client_id: Joi.alternatives().try(
    Joi.number().integer().required(),
    Joi.string().pattern(/^\d+$/).required()
  ).messages({
    "any.required": "Client is required.",
  }),
  client_name: Joi.string().optional().allow("", null),
});

export default function RentMultipleDevices() {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(clientSchema),
  });

  const { closeDialog } = useDialogStore();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientFields, setShowNewClientFields] = useState(false);
  const [uniqueIds, setUniqueIds] = useState([]);
  const [deviceData, setDeviceData] = useState({});
  const [expandedDevices, setExpandedDevices] = useState({});
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const handleClientIdChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9]/g, "");
    setValue("client_id", value.toString());
  };

  const handleUniqueIdsChange = (e) => {
    const text = e.target.value;
    const ids = text
      .split("\n")
      .map((id) => id.trim())
      .filter((id) => id !== "");
    setUniqueIds(ids);
    
    const newDeviceData = {};
    ids.forEach((id) => {
      if (!deviceData[id]) {
        newDeviceData[id] = {
          monthly_rental_price: "",
          black_white_price: "",
          color_price: "",
          black_white_counter: "",
          color_counter: "",
          location: "",
        };
      } else {
        newDeviceData[id] = deviceData[id];
      }
    });
    setDeviceData(newDeviceData);
  };

  const handleDeviceDataChange = (uniqueId, field, value) => {
    setDeviceData((prev) => ({
      ...prev,
      [uniqueId]: {
        ...prev[uniqueId],
        [field]: value,
      },
    }));
  };

  const toggleDeviceExpanded = (uniqueId) => {
    setExpandedDevices((prev) => ({
      ...prev,
      [uniqueId]: !prev[uniqueId],
    }));
  };

  useEffect(() => {
    let timeoutId;
    setShowLoading(true);

    const fetchClients = async () => {
      try {
        const response = await axiosInstance.post(
          `${window.REACT_APP_API_ENDPOINT}/users/fetch-users`,
          query.trim() !== "" ? { q: query } : {}
        );
        setClients(response.data.data);
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setShowLoading(false);
      }
    };

    timeoutId = setTimeout(fetchClients, 1500);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const mutation = useMutation({
    mutationFn: async (formData) => {
      return await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/store-bulk`,
        formData
      );
    },
    onSuccess: (response) => {
      const rentalsProcessed = response.data.data?.length > 0;
      
      if (rentalsProcessed) {
        toast.success(response.data.message || "Devices processed successfully!");
      }
      
      const failedDevices = response.data.failed_devices || [];
      failedDevices.forEach((device) => {
        toast.error(
          `Device ${device.unique_id} (S/N: ${device.serial_number}) - ${device.reason}`,
          { position: "bottom-right", autoClose: 5000 }
        );
      });

      const skipped = response.data.skipped || {};
      const noSerialDevices = skipped.no_serial_number || [];
      noSerialDevices.forEach((device) => {
        toast.warning(
          `Device ${device.unique_id} - ${device.reason}`,
          { position: "bottom-right", autoClose: 5000 }
        );
      });

      const reservedSoldDevices = skipped.reserved_or_sold || [];
      reservedSoldDevices.forEach((device) => {
        toast.warning(
          `Device ${device.unique_id} (S/N: ${device.serial_number}) - ${device.reason}`,
          { position: "bottom-right", autoClose: 5000 }
        );
      });
      
      queryClient.invalidateQueries({ queryKey: ["devices-not-rented"] });
      closeDialog();
    },
    onError: (error) => {
      const errorData = error?.response?.data;
      
      if (errorData?.failed_devices) {
        errorData.failed_devices.forEach((device) => {
          toast.error(
            `Device ${device.unique_id} (S/N: ${device.serial_number}) - ${device.reason}`,
            { position: "bottom-right", autoClose: 5000 }
          );
        });
      } else {
        const message = errorData?.message || "An error occurred. Please try again.";
        toast.error(message);
      }
    },
  });

  const onSubmit = (data) => {
    if (!data.client_id || uniqueIds.length === 0) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const missingFields = [];
    uniqueIds.forEach((id) => {
      if (!deviceData[id].monthly_rental_price) missingFields.push(`${id}: Monthly price required`);
      if (!deviceData[id].black_white_price) missingFields.push(`${id}: B&W price required`);
      if (!deviceData[id].color_price) missingFields.push(`${id}: Color price required`);
    });

    if (missingFields.length > 0) {
      toast.error("Missing required fields:\n" + missingFields.join("\n"));
      return;
    }

    const clientId = showNewClientFields ? `A${data.client_id}` : data.client_id.toString();
    
    const devicesDataWithContact = Object.keys(deviceData).reduce((acc, id) => {
      acc[id] = {
        ...deviceData[id],
        email,
        phone,
      };
      return acc;
    }, {});

    mutation.mutate({
      client_id: clientId,
      client_name: data.client_name || null,
      unique_ids: uniqueIds,
      devices_data: devicesDataWithContact,
    });
  };

  return (
    <form
      className="max-w-4xl mx-auto bg-white p-6 rounded-lg space-y-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Client */}
      <div>
        <label className="block font-medium">
          Client <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <Combobox
            as="div"
            value={selectedClient}
            onChange={(client) => {
              setSelectedClient(client);
              setValue("client_id", client.id);
              setValue("client_name", client.name);
              setShowNewClientFields(false);
            }}
            className="w-full"
          >
            <div className="relative">
              <Combobox.Input
                className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                onChange={(event) => setQuery(event.target.value)}
                displayValue={(client) => client?.name || ""}
                placeholder="Search for a client..."
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </Combobox.Button>

              {showLoading && query.trim() !== "" && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <Combobox.Option
                    key="loading"
                    className="relative cursor-default select-none py-2 pl-3 pr-9 bg-indigo-600 text-white"
                    value={null}
                    disabled
                  >
                    <span className="block truncate">Loading...</span>
                  </Combobox.Option>
                </Combobox.Options>
              )}

              {clients.length > 0 && !showLoading && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {clients.map((client) => (
                    <Combobox.Option
                      key={client.id}
                      value={client}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-3 pr-9 ${
                          active ? "bg-indigo-600 text-white" : "text-gray-900"
                        }`
                      }
                    >
                      {({ active, selected }) => (
                        <>
                          <span className={`block truncate ${selected ? "font-semibold" : ""}`}>
                            {client.name}
                          </span>
                          {selected && (
                            <span
                              className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                active ? "text-white" : "text-indigo-600"
                              }`}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}

              {clients.length === 0 && query.trim() !== "" && !showLoading && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <Combobox.Option
                    key="no-user"
                    className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900"
                    value={null}
                    disabled
                  >
                    <span className="block truncate">No client found</span>
                  </Combobox.Option>
                </Combobox.Options>
              )}
            </div>
          </Combobox>
          <button
            className={`${
              showNewClientFields ? "bg-red-500" : "bg-green-500"
            } text-white w-9 h-9 rounded-md flex items-center justify-center`}
            type="button"
            onClick={() => setShowNewClientFields(!showNewClientFields)}
          >
            {!showNewClientFields ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </button>
        </div>
        {showNewClientFields && (
          <div className="mt-2 space-y-2">
            <div>
              <label className="block font-medium">
                New Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register("client_name")}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
              />
              {errors.client_name && (
                <p className="text-red-500 text-sm">
                  {errors.client_name.message}
                </p>
              )}
            </div>
            <div>
              <label className="block font-medium">
                New Client ID <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500">A</span>
                </div>
                <input
                  type="text"
                  {...register("client_id")}
                  onChange={handleClientIdChange}
                  className="w-full border border-gray-300 pl-8 px-3 py-2 rounded-md"
                  placeholder="Enter numbers"
                />
              </div>
              {errors.client_id && (
                <p className="text-red-500 text-sm">
                  {errors.client_id.message}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Numbers only (A will be added automatically)
              </p>
            </div>
          </div>
        )}
        {errors.client_id && !showNewClientFields && (
          <p className="text-red-500 text-sm mt-1">{errors.client_id.message}</p>
        )}
      </div>

      {/* Unique IDs textarea */}
      <div>
        <label className="block font-medium">
          Unique IDs (one per line) <span className="text-red-500">*</span>
        </label>
        <textarea
          onChange={handleUniqueIdsChange}
          rows={6}
          placeholder={`00001\n00002\n00003`}
          className="w-full border border-gray-300 px-3 py-2 rounded-md"
        />
        {errors.unique_ids && (
          <p className="text-red-500 text-sm">{errors.unique_ids.message}</p>
        )}
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block font-medium">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-md"
            placeholder="Optional"
          />
        </div>
        <div>
          <label className="block font-medium">
            Phone
          </label>
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-md"
            placeholder="Optional"
          />
        </div>
      </div>

      {/* Device data sections */}
      {uniqueIds.length > 0 && (
        <div className="space-y-3 border-t pt-4">
          <h3 className="font-semibold text-lg mb-4">Device Details</h3>
          {uniqueIds.map((uniqueId) => (
            <div
              key={uniqueId}
              className="border border-gray-300 rounded-lg overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleDeviceExpanded(uniqueId)}
                className="w-full bg-gray-50 hover:bg-gray-100 px-4 py-3 flex items-center justify-between"
              >
                <span className="font-medium text-gray-900">{uniqueId}</span>
                {expandedDevices[uniqueId] ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                )}
              </button>

              {expandedDevices[uniqueId] && (
                <div className="px-4 py-4 space-y-3 bg-white">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block font-medium text-sm mb-1">
                        Monthly Price <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={deviceData[uniqueId]?.monthly_rental_price || ""}
                        onChange={(e) =>
                          handleDeviceDataChange(
                            uniqueId,
                            "monthly_rental_price",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-sm mb-1">
                        B&W Price <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={deviceData[uniqueId]?.black_white_price || ""}
                        onChange={(e) =>
                          handleDeviceDataChange(
                            uniqueId,
                            "black_white_price",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-sm mb-1">
                        Color Price <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={deviceData[uniqueId]?.color_price || ""}
                        onChange={(e) =>
                          handleDeviceDataChange(
                            uniqueId,
                            "color_price",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-medium text-sm mb-1">
                        B&W Counter
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={deviceData[uniqueId]?.black_white_counter || ""}
                        onChange={(e) =>
                          handleDeviceDataChange(
                            uniqueId,
                            "black_white_counter",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-sm mb-1">
                        Color Counter
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={deviceData[uniqueId]?.color_counter || ""}
                        onChange={(e) =>
                          handleDeviceDataChange(
                            uniqueId,
                            "color_counter",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={deviceData[uniqueId]?.location || ""}
                      onChange={(e) =>
                        handleDeviceDataChange(uniqueId, "location", e.target.value)
                      }
                      className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        isLoading={mutation.isPending}
      >
        {mutation.isPending ? "Submitting..." : "Rent Devices"}
      </Button>
    </form>
  );
}